import { useReorderItems } from 'components/WorkspaceEditor/hooks/useReorderItems'
import {
    useIsMoveBlockMenuDisplayed,
    useShallowHoveredItems,
} from 'components/WorkspaceEditor/HoveredItemStore'
import React, { memo, useEffect } from 'react'
import { BestPracticesSettingPopover } from '../BestPracticesSettingPopover'
import { ManageOptionsPopover } from '../ManageOptionsPopover'
import MoveBlockPopover from '../MoveBlockPopover'
import { Workspace2Popover } from '../Workspace2Popover'
import { useMoveActionAvailability } from './hooks/useMoveActionAvailability'
import { TreeHandlers, TreeStates } from './types'

interface TreeViewPopoversProps extends TreeHandlers {
    states: TreeStates
}

export const TreeViewPopovers = memo<TreeViewPopoversProps>(
    ({ states, ...handlers }) => {
        const containerRef = React.useRef<HTMLDivElement>(null)
        const { handleMoveBlock } = useReorderItems()
        const {
            isMainMenuDisplayed,
            selectedMainMenuNode,
            isBPMenuDisplayed,
            isManageOptionsDisplayed,
            setIsMoveBlockMenuDisplayed,
        } = useShallowHoveredItems((state) => ({
            isMainMenuDisplayed: state.isMainMenuDisplayed,
            selectedMainMenuNode: state.selectedMainMenuNode,
            isBPMenuDisplayed: state.isBPMenuDisplayed,
            isManageOptionsDisplayed: state.isManageOptionsDisplayed,
            setIsMoveBlockMenuDisplayed:
                state.actions.setIsMoveBlockMenuDisplayed,
        }))
        const isMoveBlockMenuDisplayed = useIsMoveBlockMenuDisplayed()
        const { canMoveDown, canMoveUp } = useMoveActionAvailability()

        /**
         * Adds an event listener to detect clicks outside the container.
         * If a click is detected outside, it will close the Move Block menu.
         */
        useEffect(() => {
            const handleMouseDown = (event: MouseEvent) => {
                if (
                    containerRef.current &&
                    !containerRef.current.contains(event.target as Node)
                ) {
                    setIsMoveBlockMenuDisplayed(false)
                }
            }

            document.addEventListener('mousedown', handleMouseDown)
            return () => {
                document.removeEventListener('mousedown', handleMouseDown)
            }
        }, [containerRef, setIsMoveBlockMenuDisplayed])

        return (
            <div
                ref={containerRef}
                onMouseDown={(event) => event.stopPropagation()}
            >
                {isMainMenuDisplayed && (
                    <Workspace2Popover
                        isActionPopoverDisplayed={isMainMenuDisplayed}
                        onCloseMainMenu={handlers.onCloseMainMenu}
                        onClickManageOptions={handlers.onClickManageOptions}
                        onClickBPMenu={handlers.onClickBPMenu}
                        onClickNotesMenu={handlers.onClickNotesMenu}
                        onClickMoveBlockMenu={handlers.onClickMoveBlockMenu}
                        anchorElement={states.dragIconElement}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        //TODO: update permissions hook
                        shouldAllowBPChange={true}
                    />
                )}
                {isBPMenuDisplayed && (
                    <BestPracticesSettingPopover
                        anchorElement={states.menuElement}
                        isBestPractice={!!selectedMainMenuNode?.isBestPractice}
                        isGlobalBestPractice={
                            !!selectedMainMenuNode?.isGlobalBestPractice
                        }
                        isBestPracticeEditDisabled={false}
                    />
                )}
                {isManageOptionsDisplayed && (
                    <ManageOptionsPopover
                        anchorElement={states.menuElement}
                        isOpen={isManageOptionsDisplayed}
                        handleCancel={handlers.onCancelManageOptions}
                        handleSave={handlers.onSaveManageOptions}
                    />
                )}
                {isMoveBlockMenuDisplayed && (
                    <MoveBlockPopover
                        anchorElement={states.menuElement}
                        isOpen={isMoveBlockMenuDisplayed}
                        onClose={() => {
                            setIsMoveBlockMenuDisplayed(false)
                        }}
                        onMoveUp={() => handleMoveBlock('up')}
                        onMoveDown={() => handleMoveBlock('down')}
                        isMoveUpDisabled={canMoveUp}
                        isMoveDownDisabled={canMoveDown}
                    />
                )}
            </div>
        )
    }
)
